import type { Zone, ZoneInput } from '@aimmo/contracts';
import { zoneCreateSchema } from '@aimmo/contracts';
import { Badge, defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';
import { resolveComune, searchComuni } from './geo-lookups.js';

/**
 * Agency-defined zones over ISTAT comuni (docs/domain/02-geo.md).
 *
 * The optional boundary polygon is not edited here on purpose: M1 ships the
 * CRUD, and drawing on a map arrives with the property map widget in M3. The
 * list reports whether a zone has one so it is visible that the column exists.
 */
export const zonesResource = defineResource<Zone, ZoneInput, ZoneInput, Partial<ZoneInput>>({
  key: 'zones',
  label: { one: 'Zona', many: 'Zone', gender: 'f' },
  client: tsRestResourceClient(api.zones),
  getRowId: (row) => row.id,
  defaultState: { sort: 'name', order: 'asc' },
  search: { placeholder: 'Cerca per nome…' },
  columns: [
    { id: 'name', header: 'Zona', sortKey: 'name' },
    {
      id: 'comune',
      header: 'Comune',
      sortKey: 'comuneName',
      accessor: (row) => `${row.comuneName} (${row.sigla})`,
    },
    {
      id: 'hasGeometry',
      header: 'Confine',
      width: '9rem',
      cell: ({ row }) =>
        row.hasGeometry ? (
          <Badge tone="success">Disegnato</Badge>
        ) : (
          <Badge tone="neutral">Assente</Badge>
        ),
    },
    {
      id: 'createdAt',
      header: 'Creata',
      sortKey: 'createdAt',
      width: '10rem',
      hiddenByDefault: true,
      accessor: (row) => new Date(row.createdAt),
    },
  ],
  filters: [
    {
      id: 'comuneId',
      label: 'Comune',
      type: 'relation',
      search: searchComuni,
      resolve: resolveComune,
    },
  ],
  form: {
    schema: zoneCreateSchema,
    defaultValues: { comuneId: '', name: '' },
    sections: [
      {
        id: 'main',
        title: 'Zona',
        fields: [
          {
            name: 'comuneId',
            label: 'Comune',
            type: 'relation',
            required: true,
            search: searchComuni,
            resolve: resolveComune,
          },
          {
            name: 'name',
            label: 'Nome',
            type: 'text',
            required: true,
            placeholder: 'Centro storico',
          },
        ],
      },
    ],
  },
  detail: {
    title: (row) => `${row.name} · ${row.comuneName}`,
    sections: [
      {
        id: 'main',
        title: 'Dati',
        fields: [
          { id: 'name', label: 'Nome' },
          { id: 'comune', label: 'Comune', accessor: (row) => `${row.comuneName} (${row.sigla})` },
          {
            id: 'hasGeometry',
            label: 'Confine',
            accessor: (row) => (row.hasGeometry ? 'Disegnato' : 'Assente'),
          },
          { id: 'createdAt', label: 'Creata', accessor: (row) => new Date(row.createdAt) },
        ],
      },
    ],
  },
  empty: {
    title: 'Nessuna zona',
    description: 'Le zone suddividono un comune. Creane una per iniziare.',
  },
});
